import interstellarApi from './interstellarApi';

export type ReportType =
  | 'call_quality'
  | 'interference'
  | 'dropped_call'
  | 'echo'
  | 'one_way_audio'
  | 'other';

export type ReportValue = 'positive' | 'negative';

export type ReportStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed';

export interface DiagnosticData {
  timestamp?: string;
  network?: {
    online?: boolean;
    effectiveType?: string | null;
    downlink?: number | null;
    rtt?: number | null;
    type?: string | null;
    latencyMs?: number | null;
    error?: string;
  };
  audio?: {
    inputDevices?: { label: string; deviceId: string }[];
    outputDevices?: { label: string; deviceId: string }[];
    inputCount?: number;
    outputCount?: number;
    error?: string;
  };
  system?: {
    browser?: string;
    os?: string;
    language?: string;
    hardwareConcurrency?: number | null;
    error?: string;
  };
}

export interface CallReport {
  id: number;
  user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  account_id: number;
  freeswitch_cdr_id: string;
  report_type: ReportType;
  report_value: ReportValue;
  note: string | null;
  status: ReportStatus;
  resolved_note: string | null;
  diagnostic_data: DiagnosticData | null;
  recording_url: string | null;
  created_at: string | null;
  modified_at: string | null;
}

export interface CallReportsListResponse {
  total_count: number;
  page: number;
  page_size: number;
  reports: CallReport[];
}

export interface FetchCallReportsOptions {
  page?: number;
  pageSize?: number;
  reportType?: string;
  reportValue?: ReportValue;
  userId?: number;
  reportStatus?: ReportStatus;
  dateFrom?: string;
  dateTo?: string;
}

export const fetchCallReports = async (
  accountId: number,
  options?: FetchCallReportsOptions
): Promise<CallReportsListResponse> => {
  const response = await interstellarApi.get<CallReportsListResponse>('/call-report', {
    params: {
      account_id: accountId,
      page: options?.page ?? 1,
      page_size: options?.pageSize ?? 50,
      ...(options?.reportType ? { report_type: options.reportType } : {}),
      ...(options?.reportValue ? { report_value: options.reportValue } : {}),
      ...(options?.userId ? { user_id: options.userId } : {}),
      ...(options?.reportStatus ? { report_status: options.reportStatus } : {}),
      ...(options?.dateFrom ? { date_from: options.dateFrom } : {}),
      ...(options?.dateTo ? { date_to: options.dateTo } : {}),
    },
  });
  return response.data;
};

export const bulkResolveReports = async (
  accountId: number,
  resolvedNote?: string
): Promise<{ message: string; updated_count: number }> => {
  const response = await interstellarApi.put<{ message: string; updated_count: number }>(
    '/call-report/bulk-resolve',
    null,
    {
      params: {
        account_id: accountId,
        ...(resolvedNote ? { resolved_note: resolvedNote } : {}),
      },
    }
  );
  return response.data;
};

export interface UpdateCallReportPayload {
  status?: ReportStatus;
  resolved_note?: string;
  report_type?: ReportType;
  report_value?: ReportValue;
  note?: string;
}

export const updateCallReport = async (
  reportId: number,
  payload: UpdateCallReportPayload
): Promise<{ message: string; id: number }> => {
  const response = await interstellarApi.put<{ message: string; id: number }>(
    `/call-report/${reportId}`,
    payload
  );
  return response.data;
};

export interface SatisfactionSummary {
  total_reports: number;
  positive: number;
  negative: number;
  satisfaction_rate: number | null;
}

export interface SatisfactionTimelineEntry {
  period: string;
  total: number;
  positive: number;
  negative: number;
  satisfaction_rate: number;
}

export interface SatisfactionByType {
  report_type: ReportType;
  total: number;
  positive: number;
  negative: number;
  satisfaction_rate: number;
}

export interface SatisfactionMetrics {
  account_id: number;
  summary: SatisfactionSummary;
  timeline: SatisfactionTimelineEntry[];
  by_type: SatisfactionByType[];
}

export interface FetchSatisfactionOptions {
  userId?: number;
  reportType?: string;
  dateFrom?: string;
  dateTo?: string;
  groupBy?: 'day' | 'week' | 'month';
}

export const fetchSatisfactionMetrics = async (
  accountId: number,
  options?: FetchSatisfactionOptions
): Promise<SatisfactionMetrics> => {
  const response = await interstellarApi.get<SatisfactionMetrics>('/call-report/satisfaction', {
    params: {
      account_id: accountId,
      ...(options?.userId ? { user_id: options.userId } : {}),
      ...(options?.reportType ? { report_type: options.reportType } : {}),
      ...(options?.dateFrom ? { date_from: options.dateFrom } : {}),
      ...(options?.dateTo ? { date_to: options.dateTo } : {}),
      group_by: options?.groupBy ?? 'day',
    },
  });
  return response.data;
};
